import React, { useEffect, forwardRef } from "react";
import "./index.less";
import icon_close from "./assets/close.png";

/**
 * 底部弹窗模态组件
 *
 * 防滚动穿透功能说明：
 *
 * 1. 页面滚动控制：
 *    - 弹窗显示时自动锁定 body 滚动
 *    - 弹窗关闭时恢复页面滚动
 *    - 防止页面背景在弹窗打开时继续滚动
 *    - 支持通过 pageClassName 控制特定页面元素滚动
 *
 */

interface PopupBottomModalProps {
  visible: boolean;
  title: string;
  contentClassName?: string;
  titleClassName?: string;
  pageClassName?: string; // 当前页面根元素的类名，用于控制弹窗显示时，根元素不可滚动
  children: React.ReactNode;
  onClose: () => void;
  onScroll?: () => void;
}

const PopupBottomModal = forwardRef(function Modal(
  {
    visible = false,
    title = "提示",
    contentClassName = "",
    titleClassName = "",
    pageClassName = "",
    children,
    onClose = () => {},
    onScroll = () => {},
  }: PopupBottomModalProps,
  ref: React.Ref<HTMLDivElement>,
) {
  useEffect(() => {
    return enableScroll;
  }, []);

  useEffect(() => {
    if (visible) {
      disableScroll();
    } else {
      enableScroll();
    }
  }, [visible]);

  /**
   * 锁定页面滚动
   *
   * 这个函数会执行以下操作：
   * 1. 将 body 的 overflow-y 设置为 hidden，防止页面滚动
   * 2. 如果指定了 pageClassName，将对应页面的滚动也锁定
   * 3. 这样可以有效防止弹窗显示时背景页面继续滚动的滚动穿透问题
   */
  const disableScroll = () => {
    // 获取弹窗页面根元素，弹窗打开时，将根元素设为不可滚动
    if (pageClassName) {
      const rootPage = document.getElementsByClassName(pageClassName)[0] as
        | HTMLElement
        | undefined;
      if (rootPage) {
        rootPage.style["overflow-y"] = "hidden";
      }
    }
    document.body.style["overflow-y"] = "hidden";
  };

  /**
   * 恢复页面滚动
   *
   * 这个函数会执行以下操作：
   * 1. 将 body 的 overflow-y 恢复为 auto，恢复页面滚动
   * 2. 如果指定了 pageClassName，将对应页面的滚动也恢复
   * 3. 确保弹窗关闭后页面滚动功能正常
   */
  const enableScroll = () => {
    // 弹窗关闭时，恢复根元素可滚动
    if (pageClassName) {
      const rootPage = document.getElementsByClassName(pageClassName)[0] as
        | HTMLElement
        | undefined;
      if (rootPage) {
        rootPage.style["overflow-y"] = "auto";
      }
    }
    document.body.style["overflow-y"] = "auto";
  };

  return (
    <div
      className={`popup-bottom-modal-common ${visible ? "show popup-bottom-modal-common-show" : ""}`}
    >
      <div
        className={`modal ${visible ? "show" : ""}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }}
      ></div>
      <div
        className={`content ${contentClassName} ${visible ? "show" : ""}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        ref={ref}
        onScroll={onScroll}
      >
        {!!title && (
          <div className="title-bar">
            <img
              src={icon_close}
              alt=""
              className="icon-close"
              onClick={onClose}
            />
            <div className={`title ${titleClassName}`}>{title}</div>
          </div>
        )}
        {children}
      </div>
    </div>
  );
});

export default PopupBottomModal;
